import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { materializeMegaMenuVisual } from "./megaMenuVisualOwnership";

function cleanText(value) {
  return String(value ?? "").trim();
}

function itemCountLabel(count, catalogMode) {
  if (catalogMode === "services") return `${count} ${count === 1 ? "servicio" : "servicios"}`;
  if (catalogMode === "products") return `${count} ${count === 1 ? "producto" : "productos"}`;
  return `${count} ${count === 1 ? "opción" : "opciones"}`;
}

function resolveMegaMenuStyle(style, catalogMode, productCount) {
  const configured = cleanText(style).toLowerCase();
  if (["elegant", "catalog", "showcase", "minimal", "editorial"].includes(configured)) return configured;
  if (productCount > 0 && productCount <= 3) return "showcase";
  if (catalogMode === "services") return "elegant";
  return "catalog";
}

function resolveMegaMenuLayout(navigation) {
  return cleanText(navigation?.megaMenuLayout).toLowerCase() === "stacked" ? "stacked" : "grid";
}

function resolveMegaMenuColumns(navigation) {
  const candidate = cleanText(navigation?.megaMenuColumns).toLowerCase();
  return ["2", "3", "4"].includes(candidate) ? Number(candidate) : null;
}

function megaMenuAnimationSx(value) {
  const animation = cleanText(value).toLowerCase() || "slide";
  if (animation === "none") return { animation: "none" };
  if (animation === "fade") {
    return {
      animation: "navbarMegaFadeIn .18s ease-out",
      "@keyframes navbarMegaFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    };
  }
  if (animation === "zoom") {
    return {
      transformOrigin: "top center",
      animation: "navbarMegaZoomIn .2s ease-out",
      "@keyframes navbarMegaZoomIn": {
        from: { opacity: 0, transform: "scale(.985) translateY(-2px)" },
        to: { opacity: 1, transform: "scale(1) translateY(0)" },
      },
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    };
  }
  return {
    animation: "navbarMegaSlideIn .18s ease-out",
    "@keyframes navbarMegaSlideIn": {
      from: { opacity: 0, transform: "translateY(-8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  };
}

function cardColumnsSx(cardsLength, navigation) {
  const stacked = resolveMegaMenuLayout(navigation) === "stacked";
  if (stacked) return "1fr";
  const configuredColumns = resolveMegaMenuColumns(navigation);
  const count = Math.max(1, Math.min(cardsLength || 1, configuredColumns || 3));
  return `repeat(${count}, minmax(0,1fr))`;
}

function categoryColumnsSx(navigation) {
  if (resolveMegaMenuLayout(navigation) === "stacked") return "1fr";
  const configuredColumns = resolveMegaMenuColumns(navigation);
  if (configuredColumns) return `repeat(${configuredColumns}, minmax(0,1fr))`;
  return { md: "repeat(3, minmax(0,1fr))", xl: "repeat(4, minmax(0,1fr))" };
}

function visualContract(navigation) {
  return materializeMegaMenuVisual({ ...navigation, megaMenuStyle: "" }).megaMenuVisual;
}

function LoadingState({ navigation, visual }) {
  const label = cleanText(navigation?.loadingCategoriesText);
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 3.5 }}>
      <CircularProgress size={19} sx={{ color: visual.loading.spinnerColor }} />
      {label ? (
        <Typography data-landing-field="navigation.loadingCategoriesText" sx={{ color: visual.loading.textColor }}>
          {label}
        </Typography>
      ) : null}
    </Stack>
  );
}

function EmptyState({ navigation, visual }) {
  const label = cleanText(navigation?.emptyCategoriesText);
  if (!label) return null;
  return (
    <Box
      sx={{
        minHeight: 110,
        display: "grid",
        placeItems: "center",
        border: "1px dashed",
        borderColor: visual.empty.borderColor,
        borderRadius: 3,
        bgcolor: visual.empty.backgroundColor,
        textAlign: "center",
        px: 3,
      }}
    >
      <Typography data-landing-field="navigation.emptyCategoriesText" sx={{ color: visual.empty.textColor }}>
        {label}
      </Typography>
    </Box>
  );
}

function ErrorState({ navigation, visual, error }) {
  const label = cleanText(error) || cleanText(navigation?.categoriesErrorText);
  if (!label) return null;
  return (
    <Alert
      data-landing-field="navigation.categoriesErrorText"
      severity="warning"
      sx={{
        bgcolor: visual.error.backgroundColor,
        color: visual.error.textColor,
        border: "1px solid",
        borderColor: visual.error.borderColor,
        "& .MuiAlert-icon": { color: visual.error.iconColor },
      }}
    >
      {label}
    </Alert>
  );
}

function AllItemsButton({ label, onClick, style }) {
  if (!label) return null;
  return (
    <Button
      data-landing-field="navigation.allProductsLabel"
      onClick={onClick}
      endIcon={<ArrowForwardRoundedIcon />}
      sx={{
        minHeight: 40,
        px: 1.35,
        borderRadius: 999,
        border: "1px solid",
        borderColor: style.borderColor,
        color: style.textColor,
        bgcolor: style.backgroundColor,
        fontWeight: 850,
        flexShrink: 0,
        "&:hover": {
          color: style.hoverTextColor,
          bgcolor: style.hoverBackgroundColor,
          borderColor: style.hoverBorderColor,
        },
      }}
    >
      {label}
    </Button>
  );
}

function IntroCopy({ eyebrow, title, description, visual, intro = false }) {
  if (!eyebrow && !title && !description) return null;
  return (
    <Stack spacing={0.65} sx={{ minWidth: 0 }}>
      {eyebrow ? (
        <Typography
          data-landing-field="navigation.megaMenuEyebrow"
          variant="overline"
          sx={{
            color: intro ? visual.eyebrow.introColor : visual.eyebrow.color,
            fontWeight: 900,
            letterSpacing: ".12em",
            lineHeight: 1.2,
          }}
        >
          {eyebrow}
        </Typography>
      ) : null}
      {title ? (
        <Typography
          data-landing-field="navigation.megaMenuTitle"
          variant="h5"
          sx={{
            color: intro ? visual.title.introColor : visual.title.color,
            fontWeight: 950,
            letterSpacing: "-.035em",
            lineHeight: 1.08,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {description ? (
        <Typography
          data-landing-field="navigation.megaMenuDescription"
          sx={{
            color: intro ? visual.description.introColor : visual.description.color,
            lineHeight: 1.5,
            fontSize: ".88rem",
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Stack>
  );
}

function Stats({ productCount, categoriesCount, catalogMode, visual, intro = false, compact = false }) {
  const style = intro ? visual.introStatsChip : visual.statsChip;
  if (compact) {
    return (
      <Typography variant="caption" sx={{ display: "block", mt: 0.9, color: visual.showcaseStatsText.color }}>
        {itemCountLabel(productCount, catalogMode)}
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.6} sx={{ mt: 1.25 }}>
      <Chip size="small" label={itemCountLabel(productCount, catalogMode)} sx={{ color: style.textColor, bgcolor: style.backgroundColor, border: "1px solid", borderColor: style.borderColor }} />
      <Chip size="small" label={`${categoriesCount} ${categoriesCount === 1 ? "categoría" : "categorías"}`} sx={{ color: style.textColor, bgcolor: style.backgroundColor, border: "1px solid", borderColor: style.borderColor }} />
    </Stack>
  );
}

function PreviewCard({ product, onProduct, visual, elegant = false, stacked = false }) {
  const image = cleanText(product?.image);
  const description = cleanText(product?.description);
  const category = cleanText(product?.category);
  const card = visual.previewCard;
  return (
    <Button
      onClick={() => onProduct(product.path)}
      sx={{
        p: 0,
        display: stacked ? "flex" : "block",
        alignItems: stacked ? "stretch" : undefined,
        minWidth: 0,
        textAlign: "left",
        color: card.titleTextColor,
        borderRadius: elegant ? 3.2 : 2.5,
        minHeight: stacked ? 108 : undefined,
        overflow: "hidden",
        border: "1px solid",
        borderColor: card.borderColor,
        bgcolor: card.backgroundColor,
        boxShadow: card.boxShadow,
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: card.hoverBorderColor,
          boxShadow: card.hoverBoxShadow,
          bgcolor: card.backgroundColor,
        },
      }}
    >
      {image ? (
        <Box component="img" src={image} alt="" loading="lazy" decoding="async" onError={(event) => { event.currentTarget.style.display = "none"; }} sx={{ width: stacked ? 124 : "100%", minWidth: stacked ? 124 : undefined, height: stacked ? 108 : (elegant ? 146 : 128), display: "block", objectFit: "cover", bgcolor: card.imageBackgroundColor }} />
      ) : null}
      <Box sx={{ px: elegant ? 1.8 : 1.45, py: elegant ? 1.55 : 1.3, minWidth: 0, flex: stacked ? 1 : undefined }}>
        {category ? <Typography variant="caption" noWrap sx={{ display: "block", mb: 0.4, color: card.categoryTextColor, fontWeight: 750 }}>{category}</Typography> : null}
        <Typography noWrap sx={{ color: card.titleTextColor, fontSize: elegant ? ".98rem" : ".9rem", lineHeight: 1.2, fontWeight: 900 }}>{product.name}</Typography>
        {description ? <Typography sx={{ mt: 0.6, color: card.descriptionTextColor, fontSize: ".78rem", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{description}</Typography> : null}
      </Box>
    </Button>
  );
}

function CategoryRow({ category, catalogMode, showCount, onCategory, onSubcategory, visual }) {
  const row = visual.categoryRow;
  const sub = visual.subcategory;
  return (
    <Box sx={{ borderRadius: 2, border: "1px solid", borderColor: row.borderColor, bgcolor: row.backgroundColor, "&:hover": { bgcolor: row.hoverBackgroundColor, borderColor: row.hoverBorderColor } }}>
      <Button
        fullWidth
        onClick={() => onCategory(category.query || { category: category.value })}
        endIcon={<ChevronRightRoundedIcon sx={{ fontSize: "18px !important", color: row.iconColor }} />}
        sx={{ minHeight: 46, px: 1.05, py: 0.55, justifyContent: "space-between", borderRadius: 2, color: row.labelColor, textAlign: "left" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: ".86rem", fontWeight: 850, color: row.labelColor }}>{category.label}</Typography>
          {showCount ? <Typography variant="caption" sx={{ color: row.countColor }}>{itemCountLabel(category.count, catalogMode)}</Typography> : null}
        </Box>
      </Button>
      {category.subcategories?.length ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.4} sx={{ px: 1.05, pb: 0.8 }}>
          {category.subcategories.slice(0, 5).map((subcategory) => (
            <Button key={`${category.value}-${subcategory.value}`} onClick={() => onSubcategory(subcategory.query || { category: category.value, subcategory: subcategory.value })} sx={{ minWidth: 0, p: 0, color: sub.textColor, bgcolor: sub.backgroundColor, fontSize: ".7rem", fontWeight: 720, textTransform: "none", "&:hover": { color: sub.hoverTextColor, bgcolor: sub.hoverBackgroundColor } }}>
              {subcategory.label}
            </Button>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function CategoryPills({ categories, onCategory, visual, limit = 8 }) {
  if (!categories.length) return null;
  const pill = visual.categoryPill;
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
      {categories.slice(0, limit).map((category) => (
        <Button key={category.value} onClick={() => onCategory(category.query || { category: category.value })} sx={{ px: 1.05, py: 0.4, minHeight: 32, borderRadius: 999, color: pill.textColor, bgcolor: pill.backgroundColor, border: "1px solid", borderColor: pill.borderColor, fontWeight: 760, fontSize: ".74rem", "&:hover": { color: pill.hoverTextColor, bgcolor: pill.hoverBackgroundColor, borderColor: pill.hoverBorderColor } }}>
          {category.label}
        </Button>
      ))}
    </Stack>
  );
}

function ElegantMenu(props) {
  const { navigation, displayMode, categories, latestProducts, productCount, catalogMode, loading, error, onAllProducts, onCategory, onProduct } = props;
  const visual = visualContract(navigation);
  const eyebrow = cleanText(navigation?.megaMenuEyebrow);
  const title = cleanText(navigation?.megaMenuTitle);
  const description = cleanText(navigation?.megaMenuDescription);
  const productsLabel = cleanText(navigation?.productsLabel);
  const categoriesSubtitle = cleanText(navigation?.megaMenuCategoriesSubtitle);
  const allProductsLabel = cleanText(navigation?.allProductsLabel);
  const showStats = navigation?.showMegaMenuStats === true;
  const showIntro = navigation?.showMegaMenuIntro === true && (eyebrow || title || description || showStats);
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  const cards = showItems ? latestProducts.slice(0, 3) : [];
  return (
    <Box sx={{ p: { md: 3, lg: 3.5 } }}>
      {(showIntro || allProductsLabel) ? <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={3} sx={{ mb: 2.2 }}>
        {showIntro ? <Box sx={{ maxWidth: 670 }}><IntroCopy eyebrow={eyebrow} title={title} description={description} visual={visual} />{showStats ? <Stats productCount={productCount} categoriesCount={categories.length} catalogMode={catalogMode} visual={visual} /> : null}</Box> : <Box />}
        <AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} />
      </Stack> : null}
      {loading ? <LoadingState navigation={navigation} visual={visual} /> : error ? <ErrorState navigation={navigation} visual={visual} error={error} /> : <>
        {cards.length ? <Box sx={{ display: "grid", gridTemplateColumns: cardColumnsSx(cards.length, navigation), gap: 1.5 }}>{cards.map((product) => <PreviewCard key={product.id} product={product} onProduct={onProduct} visual={visual} elegant stacked={resolveMegaMenuLayout(navigation) === "stacked"} />)}</Box> : <EmptyState navigation={navigation} visual={visual} />}
        {showCategories && categories.length ? <Box sx={{ mt: cards.length ? 2.1 : 0 }}>
          {productsLabel || categoriesSubtitle ? <Box sx={{ mb: 0.85 }}>{productsLabel ? <Typography data-landing-field="navigation.productsLabel" sx={{ fontWeight: 900, color: visual.categoriesTitle.color }}>{productsLabel}</Typography> : null}{categoriesSubtitle ? <Typography data-landing-field="navigation.megaMenuCategoriesSubtitle" variant="body2" sx={{ color: visual.categoriesSubtitle.color }}>{categoriesSubtitle}</Typography> : null}</Box> : null}
          <CategoryPills categories={categories} onCategory={onCategory} visual={visual} />
        </Box> : null}
      </>}
    </Box>
  );
}

function CatalogMenu(props) {
  const { navigation, displayMode, categories, latestProducts, productCount, catalogMode, loading, error, onAllProducts, onCategory, onSubcategory, onProduct } = props;
  const visual = visualContract(navigation);
  const eyebrow = cleanText(navigation?.megaMenuEyebrow);
  const title = cleanText(navigation?.megaMenuTitle);
  const description = cleanText(navigation?.megaMenuDescription);
  const categoriesSubtitle = cleanText(navigation?.megaMenuCategoriesSubtitle);
  const sortLabel = cleanText(navigation?.megaMenuSortLabel);
  const latestLabel = cleanText(navigation?.megaMenuLatestLabel);
  const allProductsLabel = cleanText(navigation?.allProductsLabel);
  const productsLabel = cleanText(navigation?.productsLabel);
  const showStats = navigation?.showMegaMenuStats === true;
  const showIntro = navigation?.showMegaMenuIntro === true && (eyebrow || title || description || showStats || allProductsLabel);
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  const showLatest = showItems && navigation?.showMegaMenuLatest === true;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: showIntro ? { md: "245px minmax(0,1fr)", lg: "270px minmax(0,1fr)" } : "minmax(0,1fr)" }}>
      {showIntro ? <Box sx={{ p: { md: 2.5, lg: 2.8 }, bgcolor: visual.introSurface.backgroundColor, borderColor: visual.introSurface.borderColor, display: "flex", flexDirection: "column", minHeight: 270 }}>
        <IntroCopy eyebrow={eyebrow} title={title} description={description} visual={visual} intro />
        {showStats ? <Stats productCount={productCount} categoriesCount={categories.length} catalogMode={catalogMode} visual={visual} intro /> : null}
        {allProductsLabel ? <Box sx={{ mt: "auto", pt: 2 }}><AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.introAllItemsButton} /></Box> : null}
      </Box> : null}
      <Box sx={{ p: { md: 2.3, lg: 2.7 }, minWidth: 0 }}>
        {(productsLabel || categoriesSubtitle || sortLabel || (!showIntro && allProductsLabel)) ? <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.2 }}>
          <Box sx={{ minWidth: 0 }}>{productsLabel ? <Typography data-landing-field="navigation.productsLabel" variant="h6" sx={{ color: visual.categoriesTitle.color, fontWeight: 950, letterSpacing: "-.025em" }}>{productsLabel}</Typography> : null}{categoriesSubtitle ? <Typography data-landing-field="navigation.megaMenuCategoriesSubtitle" variant="body2" sx={{ color: visual.categoriesSubtitle.color }}>{categoriesSubtitle}</Typography> : null}{!loading && !error && categories.length && sortLabel ? <Typography data-landing-field="navigation.megaMenuSortLabel" variant="caption" sx={{ color: visual.sortLabel.color }}>{sortLabel}</Typography> : null}</Box>
          {!showIntro ? <AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} /> : null}
        </Stack> : null}
        {loading ? <LoadingState navigation={navigation} visual={visual} /> : error ? <ErrorState navigation={navigation} visual={visual} error={error} /> : ((showCategories && categories.length) || (showItems && latestProducts.length)) ? <>
          {showCategories && categories.length ? <Box sx={{ display: "grid", gridTemplateColumns: categoryColumnsSx(navigation), gap: 0.45, maxHeight: "min(44vh, 330px)", overflowY: "auto", pr: 0.45 }}>{categories.map((category) => <CategoryRow key={category.value} category={category} catalogMode={catalogMode} showCount={showStats} onCategory={onCategory} onSubcategory={onSubcategory} visual={visual} />)}</Box> : null}
          {showItems && (showLatest || !showCategories) && latestProducts.length ? <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 1.35, pt: 1.1, borderTop: "1px solid", borderColor: visual.divider.color, minWidth: 0, overflow: "hidden" }}>
            {latestLabel ? <Typography data-landing-field="navigation.megaMenuLatestLabel" variant="caption" sx={{ color: visual.latestLabel.color, fontWeight: 850, flexShrink: 0 }}>{latestLabel}</Typography> : null}
            <Stack direction="row" spacing={0.45} sx={{ minWidth: 0, overflow: "hidden" }}>{latestProducts.slice(0, 4).map((product) => <Button key={product.id} onClick={() => onProduct(product.path)} sx={{ minWidth: 0, maxWidth: 180, px: 0.9, py: 0.3, borderRadius: 999, color: visual.latestItem.textColor, bgcolor: visual.latestItem.backgroundColor, border: "1px solid", borderColor: visual.latestItem.borderColor, fontSize: ".71rem", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { color: visual.latestItem.hoverTextColor, bgcolor: visual.latestItem.hoverBackgroundColor, borderColor: visual.latestItem.hoverBorderColor } }}>{product.name}</Button>)}</Stack>
          </Stack> : null}
        </> : <EmptyState navigation={navigation} visual={visual} />}
      </Box>
    </Box>
  );
}

function ShowcaseMenu(props) {
  const { navigation, displayMode, categories, latestProducts, catalogMode, productCount, loading, error, onAllProducts, onCategory, onProduct } = props;
  const visual = visualContract(navigation);
  const eyebrow = cleanText(navigation?.megaMenuEyebrow);
  const title = cleanText(navigation?.megaMenuTitle);
  const description = cleanText(navigation?.megaMenuDescription);
  const productsLabel = cleanText(navigation?.productsLabel);
  const categoriesSubtitle = cleanText(navigation?.megaMenuCategoriesSubtitle);
  const allProductsLabel = cleanText(navigation?.allProductsLabel);
  const showStats = navigation?.showMegaMenuStats === true;
  const showIntro = navigation?.showMegaMenuIntro === true && (eyebrow || title || description || showStats);
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  const cards = showItems ? latestProducts.slice(0, 3) : [];
  return (
    <Box sx={{ p: { md: 2.8, lg: 3.2 } }}>
      {(showIntro || allProductsLabel) ? <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={3} sx={{ mb: 1.9 }}>{showIntro ? <Box sx={{ maxWidth: 650 }}><IntroCopy eyebrow={eyebrow} title={title} description={description} visual={visual} />{showStats ? <Stats productCount={productCount} categoriesCount={categories.length} catalogMode={catalogMode} visual={visual} compact /> : null}</Box> : <Box />}<AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} /></Stack> : null}
      {loading ? <LoadingState navigation={navigation} visual={visual} /> : error ? <ErrorState navigation={navigation} visual={visual} error={error} /> : cards.length ? <>
        <Box sx={{ display: "grid", gridTemplateColumns: cardColumnsSx(cards.length, navigation), gap: 1.2 }}>{cards.map((product) => <PreviewCard key={product.id} product={product} onProduct={onProduct} visual={visual} stacked={resolveMegaMenuLayout(navigation) === "stacked"} />)}</Box>
        {showCategories && categories.length ? <Box sx={{ mt: 1.55 }}>{productsLabel || categoriesSubtitle ? <Box sx={{ mb: 0.7 }}>{productsLabel ? <Typography data-landing-field="navigation.productsLabel" sx={{ fontWeight: 900, color: visual.categoriesTitle.color }}>{productsLabel}</Typography> : null}{categoriesSubtitle ? <Typography data-landing-field="navigation.megaMenuCategoriesSubtitle" variant="body2" sx={{ color: visual.categoriesSubtitle.color }}>{categoriesSubtitle}</Typography> : null}</Box> : null}<CategoryPills categories={categories} onCategory={onCategory} visual={visual} limit={6} /></Box> : null}
      </> : <EmptyState navigation={navigation} visual={visual} />}
    </Box>
  );
}


function MinimalMenu(props) {
  const { navigation, displayMode, categories, latestProducts, productCount, catalogMode, loading, error, onAllProducts, onCategory, onSubcategory, onProduct } = props;
  const visual = visualContract(navigation);
  const eyebrow = cleanText(navigation?.megaMenuEyebrow);
  const title = cleanText(navigation?.megaMenuTitle);
  const description = cleanText(navigation?.megaMenuDescription);
  const productsLabel = cleanText(navigation?.productsLabel);
  const allProductsLabel = cleanText(navigation?.allProductsLabel);
  const showStats = navigation?.showMegaMenuStats === true;
  const showIntro = navigation?.showMegaMenuIntro === true && (eyebrow || title || description || showStats);
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  const cards = showItems ? latestProducts.slice(0, resolveMegaMenuLayout(navigation) === "stacked" ? 3 : 2) : [];

  return (
    <Box sx={{ p: { md: 2.15, lg: 2.45 } }}>
      {(showIntro || allProductsLabel) ? (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2.5} sx={{ mb: 1.35 }}>
          {showIntro ? (
            <Box sx={{ maxWidth: 620 }}>
              <IntroCopy eyebrow={eyebrow} title={title} description={description} visual={visual} />
              {showStats ? <Stats productCount={productCount} categoriesCount={categories.length} catalogMode={catalogMode} visual={visual} compact /> : null}
            </Box>
          ) : <Box />}
          <AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} />
        </Stack>
      ) : null}
      {loading ? <LoadingState navigation={navigation} visual={visual} /> : error ? <ErrorState navigation={navigation} visual={visual} error={error} /> : (
        <Stack spacing={1.25}>
          {showCategories && categories.length ? (
            <Box>
              {productsLabel ? <Typography data-landing-field="navigation.productsLabel" sx={{ mb: 0.7, color: visual.categoriesTitle.color, fontWeight: 900 }}>{productsLabel}</Typography> : null}
              <Box sx={{ display: "grid", gridTemplateColumns: categoryColumnsSx(navigation), gap: 0.35 }}>
                {categories.map((category) => <CategoryRow key={category.value} category={category} catalogMode={catalogMode} showCount={false} onCategory={onCategory} onSubcategory={onSubcategory} visual={visual} />)}
              </Box>
            </Box>
          ) : null}
          {cards.length ? (
            <Box sx={{ pt: 1.05, borderTop: "1px solid", borderColor: visual.divider.color, display: "grid", gridTemplateColumns: cardColumnsSx(cards.length, navigation), gap: 0.8 }}>
              {cards.map((product) => <PreviewCard key={product.id} product={product} onProduct={onProduct} visual={visual} stacked />)}
            </Box>
          ) : (!showCategories || !categories.length) ? <EmptyState navigation={navigation} visual={visual} /> : null}
        </Stack>
      )}
    </Box>
  );
}

function EditorialMenu(props) {
  const { navigation, displayMode, categories, latestProducts, productCount, catalogMode, loading, error, onAllProducts, onCategory, onProduct } = props;
  const visual = visualContract(navigation);
  const eyebrow = cleanText(navigation?.megaMenuEyebrow);
  const title = cleanText(navigation?.megaMenuTitle);
  const description = cleanText(navigation?.megaMenuDescription);
  const productsLabel = cleanText(navigation?.productsLabel);
  const categoriesSubtitle = cleanText(navigation?.megaMenuCategoriesSubtitle);
  const allProductsLabel = cleanText(navigation?.allProductsLabel);
  const showStats = navigation?.showMegaMenuStats === true;
  const showIntro = navigation?.showMegaMenuIntro === true && (eyebrow || title || description || showStats);
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  const cards = showItems ? latestProducts.slice(0, 3) : [];

  return (
    <Box sx={{ p: { md: 2.5, lg: 3 } }}>
      {loading ? <LoadingState navigation={navigation} visual={visual} /> : error ? <ErrorState navigation={navigation} visual={visual} error={error} /> : (
        <Box sx={{ display: "grid", gridTemplateColumns: showIntro ? { md: "minmax(220px,.75fr) minmax(0,1.5fr)" } : "1fr", gap: { md: 2.4, lg: 3 } }}>
          {showIntro ? (
            <Stack spacing={1.4} sx={{ minWidth: 0, pr: { md: 1.5 }, borderRight: { md: "1px solid" }, borderColor: visual.divider.color }}>
              <IntroCopy eyebrow={eyebrow} title={title} description={description} visual={visual} />
              {showStats ? <Stats productCount={productCount} categoriesCount={categories.length} catalogMode={catalogMode} visual={visual} /> : null}
              <Box sx={{ pt: .5 }}><AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} /></Box>
            </Stack>
          ) : null}
          <Stack spacing={1.4} sx={{ minWidth: 0 }}>
            {cards.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: cardColumnsSx(cards.length, { ...navigation, megaMenuLayout: "stacked" }), gap: 0.85 }}>
                {cards.map((product) => <PreviewCard key={product.id} product={product} onProduct={onProduct} visual={visual} stacked />)}
              </Box>
            ) : null}
            {showCategories && categories.length ? (
              <Box sx={{ pt: cards.length ? 1.1 : 0, borderTop: cards.length ? "1px solid" : "none", borderColor: visual.divider.color }}>
                {productsLabel || categoriesSubtitle ? (
                  <Box sx={{ mb: .7 }}>
                    {productsLabel ? <Typography data-landing-field="navigation.productsLabel" sx={{ color: visual.categoriesTitle.color, fontWeight: 900 }}>{productsLabel}</Typography> : null}
                    {categoriesSubtitle ? <Typography data-landing-field="navigation.megaMenuCategoriesSubtitle" variant="body2" sx={{ color: visual.categoriesSubtitle.color }}>{categoriesSubtitle}</Typography> : null}
                  </Box>
                ) : null}
                <CategoryPills categories={categories} onCategory={onCategory} visual={visual} limit={8} />
              </Box>
            ) : null}
            {!cards.length && (!showCategories || !categories.length) ? <EmptyState navigation={navigation} visual={visual} /> : null}
            {!showIntro ? <Box><AllItemsButton label={allProductsLabel} onClick={onAllProducts} style={visual.allItemsButton} /></Box> : null}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default function NavbarMegaMenu({ open, navigation, displayMode = "both", categories, latestProducts, productCount, catalogMode, loading, error, onMouseEnter, onMouseLeave, onAllProducts, onCategory, onSubcategory, onProduct }) {
  if (!open) return null;
  const style = resolveMegaMenuStyle(navigation?.megaMenuStyle, catalogMode, productCount);
  const visual = visualContract(navigation);
  const borderWidth = Math.max(0, Math.min(4, Number(visual.surface.borderWidth ?? 1) || 0));
  const radius = Math.max(0, Math.min(64, Number(visual.surface.borderRadius ?? 22) || 0));
  const maxWidth = style === "minimal" ? 1080 : style === "elegant" ? 1180 : style === "editorial" ? 1240 : 1320;
  const contentProps = { navigation, displayMode, categories, latestProducts, productCount, catalogMode, loading, error, onAllProducts, onCategory, onSubcategory, onProduct };
  return (
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} sx={{ display: { xs: "none", md: "block" }, position: "absolute", top: "100%", left: 0, width: "100%", px: { md: 2, lg: 3 }, pb: 2, pointerEvents: "auto" }}>
      <Box
        data-mega-menu-style={style}
        data-mega-menu-layout={resolveMegaMenuLayout(navigation)}
        data-mega-menu-animation={cleanText(navigation?.megaMenuAnimation) || "slide"}
        sx={{
          maxWidth,
          mx: "auto",
          overflow: "hidden",
          bgcolor: visual.surface.backgroundColor,
          border: borderWidth > 0 ? `${borderWidth}px solid` : "none",
          borderColor: visual.surface.borderColor,
          borderRadius: `0 0 ${radius}px ${radius}px`,
          boxShadow: visual.surface.boxShadow,
          ...megaMenuAnimationSx(navigation?.megaMenuAnimation),
        }}
      >
        {style === "elegant" ? <ElegantMenu {...contentProps} /> : null}
        {style === "catalog" ? <CatalogMenu {...contentProps} /> : null}
        {style === "showcase" ? <ShowcaseMenu {...contentProps} /> : null}
        {style === "minimal" ? <MinimalMenu {...contentProps} /> : null}
        {style === "editorial" ? <EditorialMenu {...contentProps} /> : null}
      </Box>
    </Box>
  );
}
